import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Link2, Unplug, Radio } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const NOTICES = {
  connected: ["success", "Restream connected — your streams will go to Restream automatically"],
  cancelled: ["info", "Restream connection cancelled"],
  error: ["error", "Couldn't connect Restream — please try again"],
};

export const RestreamConnect = ({ stream, forwarding, onSaved }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  useEffect(() => { api.get("/restream/status").then((r) => setStatus(r.data)).catch(() => setStatus({ available: false })); }, []);
  useEffect(() => {
    const n = NOTICES[params.get("restream")];
    if (!n) return;
    toast[n[0]](n[1]);
    params.delete("restream"); setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.post("/restream/connect", { origin_url: window.location.origin, return_path: `/streams/${stream.id}?tab=destinations` });
      window.location.assign(r.data.authorization_url);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start Restream connection"); setBusy(""); }
  };
  const disconnect = async () => {
    setBusy("disconnect");
    try { await api.delete("/restream/connection"); setStatus((s) => ({ ...s, connected: false, username: "" })); toast.success("Restream disconnected"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't disconnect"); }
    finally { setBusy(""); }
  };
  const toggle = async (enabled) => {
    try { const r = await api.put(`/streams/${stream.id}/restream`, { enabled }); onSaved(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  if (!status) return null;
  const live = (forwarding || []).find((f) => f.id === "restream-account")?.active;
  return (
    <div data-testid="restream-card" className="rounded-xl border border-gray-800 bg-black/20 px-4 py-4 flex flex-wrap items-center gap-4">
      <div className="h-10 w-10 rounded-lg bg-[#1F2A44] border border-gray-700 flex items-center justify-center shrink-0"><Radio className="h-5 w-5 text-sky-300" /></div>
      <div className="flex-1 min-w-[200px]">
        <p className="text-sm font-semibold text-white flex items-center gap-2">
          Restream
          <span data-testid="restream-status" className={"px-2 py-0.5 rounded-full text-[11px] font-semibold border " + (status.expired ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : status.connected ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-700/40 text-gray-400 border-gray-700")}>
            {status.expired ? "Needs reconnect" : status.connected ? "Connected" : "Disconnected"}
          </span>
          {status.connected && live && <span data-testid="restream-live" className="text-[11px] text-red-400 font-semibold">● Sending now</span>}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {!status.available ? "Restream isn't available on this portal yet." :
            status.expired ? "Your Restream sign-in has expired. Reconnect so your streams keep reaching Restream." :
            status.connected ? <>Signed in as <span className="text-gray-200">{status.username || "your Restream account"}</span>. Every stream goes to Restream's nearest server.</> :
            "Connect your Restream account to send your streams to all your Restream channels at once."}
        </p>
      </div>
      {status.connected && (
        <label className="flex items-center gap-2 text-xs text-gray-300">
          <Switch data-testid="restream-stream-toggle" checked={stream.restream_enabled !== false} onCheckedChange={toggle} className="data-[state=checked]:bg-amber-500" />
          Send this stream
        </label>
      )}
      {status.available && status.expired && (
        <button data-testid="restream-reconnect" onClick={connect} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg gold-gradient text-black text-xs font-semibold hover:brightness-110 disabled:opacity-50">
          {busy === "connect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Reconnect Restream
        </button>
      )}
      {status.available && (status.connected ? (
        <button data-testid="restream-disconnect" onClick={disconnect} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 text-gray-300 hover:text-red-300 hover:border-red-500/40 text-xs font-medium transition-colors disabled:opacity-50">
          {busy === "disconnect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />} Disconnect
        </button>
      ) : (
        <button data-testid="restream-connect" onClick={connect} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg gold-gradient text-black text-xs font-semibold hover:brightness-110 disabled:opacity-50">
          {busy === "connect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Connect Restream
        </button>
      ))}
    </div>
  );
};
